import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { BRAND_CONFIG } from 'constants/branding';
import {
  CONNECTOR_SCOPES,
  CONNECTOR_TOOLS,
  CATALOGUE_TOTALS,
  type ScopeName,
} from 'constants/connectorCatalogue';

/**
 * The connector documentation, served by the portal itself.
 *
 * <h3>Why the portal and not a docs site</h3>
 * A connector directory submission needs a docs URL, and the portal is already
 * deployed to every brand's hostname from one bundle. A separate docs site would
 * be new infrastructure, a new certificate and a second thing to keep current.
 *
 * <h3>Why it is brand-resolved</h3>
 * Same reason the privacy policy is: one bundle serves several portals, so a
 * hardcoded "CatchMyCall" here would greet a Green Giraffe reader with another
 * company's product name and support address. Everything identifying comes from
 * {@link BRAND_CONFIG}, and a brand without a connector says so rather than
 * printing a server URL that belongs to someone else's listing.
 *
 * <h3>Public on purpose</h3>
 * A directory reviewer and a prospective customer both read this before they have
 * an account, so it cannot sit behind PrivateRoute.
 */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mt-8">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-2 space-y-3 text-sm leading-relaxed text-slate-700">{children}</div>
  </section>
);

/** Tools grouped under the scope that unlocks them — the unit of consent. */
const byScope = (scope: ScopeName) => CONNECTOR_TOOLS.filter((t) => t.scope === scope);

const ConnectorDocs: React.FC = () => {
  const { brandName, privacy, connector } = BRAND_CONFIG;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to sign in
        </Link>

        <header className="mt-6 border-b border-slate-200 pb-6">
          <div className="flex items-center gap-3">
            {connector.iconUrl && (
              <img
                src={connector.iconUrl}
                alt=""
                aria-hidden="true"
                className="h-10 w-10 shrink-0 rounded-lg ring-1 ring-slate-200"
              />
            )}
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Connecting an AI assistant to {brandName}
            </h1>
          </div>
          {/* Conditional, because the unconditional version read "{brand} publishes
              an MCP connector" directly above "Not available on this portal yet"
              — the page contradicted itself on every brand that does not have one. */}
          <p className="mt-2 text-sm text-slate-600">
            {connector.enabled ? (
              <>
                {brandName} publishes an MCP connector, so an assistant such as Claude or ChatGPT
                can work with your calls, contacts and campaigns on your behalf.
              </>
            ) : (
              <>
                An MCP connector lets an assistant such as Claude or ChatGPT work with your calls,
                contacts and campaigns on your behalf.
              </>
            )}
          </p>
        </header>

        {!connector.enabled ? (
          <Section title="Not available on this portal yet">
            <p>
              The connector is not offered under {brandName} at the moment. If you would like it
              enabled for your organisation, write to{' '}
              <a
                className="font-medium text-blue-700 hover:text-blue-800"
                href={`mailto:${connector.supportEmail}`}
              >
                {connector.supportEmail}
              </a>
              .
            </p>
          </Section>
        ) : (
          <>
            <Section title="What you connect to">
              <p>
                Point your assistant&rsquo;s connector settings at this URL. It speaks MCP over
                streamable HTTP and authenticates with OAuth 2.1, so there is nothing to install
                and no API key to copy.
              </p>
              <pre className="overflow-x-auto rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-800">
                {connector.serverUrl}
              </pre>
              <p>
                Your assistant will open a {brandName} sign-in page, show you exactly what it is
                asking for, and wait for you to approve. You sign in to {brandName} on our page, not
                in the assistant.
              </p>
            </Section>

            <Section title="What you are asked to approve">
              <p>
                Permissions are granted one at a time. There are {CATALOGUE_TOTALS.scopes}, and
                reading is always separate from doing — so an assistant can be allowed to tell you
                how a campaign landed without being allowed to send anything.
              </p>
              <div className="overflow-x-auto">
                <table className="mt-2 w-full border-collapse text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-300 text-xs uppercase tracking-wide text-slate-500">
                      <th className="py-2 pr-4 font-medium">Permission</th>
                      <th className="py-2 pr-4 font-medium">What it allows</th>
                      <th className="py-2 font-medium">Changes things</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CONNECTOR_SCOPES.map((s) => (
                      <tr key={s.scope} className="border-b border-slate-200 align-top">
                        <td className="py-2 pr-4 font-mono text-xs text-slate-900">{s.scope}</td>
                        <td className="py-2 pr-4 text-slate-700">{s.grants}</td>
                        <td className="py-2 text-slate-700">{s.writes ? 'Yes' : 'No'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="What the assistant can do">
              <p>
                {CATALOGUE_TOTALS.tools} tools in total: {CATALOGUE_TOTALS.reads} that only read,
                and {CATALOGUE_TOTALS.writes} that change something. Each is listed under the
                permission that unlocks it, so you can see what granting one actually hands over.
              </p>
              {CONNECTOR_SCOPES.map((s) => {
                const tools = byScope(s.scope);
                if (tools.length === 0) return null;
                return (
                  <div key={s.scope} className="mt-4">
                    <h3 className="font-mono text-xs font-semibold text-slate-900">{s.scope}</h3>
                    <ul className="mt-1 space-y-1">
                      {tools.map((t) => (
                        <li key={t.name} className="flex flex-wrap items-baseline gap-x-2">
                          <span className="font-mono text-xs text-slate-600">{t.name}</span>
                          <span className="text-slate-700">&mdash; {t.title}</span>
                          {t.writes && (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                              {t.destructive ? 'ends a call' : 'changes things'}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </Section>

            <Section title="What it cannot do">
              <p>
                An assistant only ever sees your own organisation&rsquo;s data, and only the parts
                you granted. It is not given your password: approving a connection issues a
                separate, time-limited credential carrying exactly the permissions you ticked.
              </p>
              <p>
                It cannot remove a do-not-contact entry. Adding one is offered and removing one is
                not, because the only mistake worth protecting against here is reach being widened
                by something you did not read closely.
              </p>
              <p>
                It cannot delete your contacts, leads, calls or campaigns &mdash; no tool deletes
                anything. Two tools are marked as changing something that is already live: one
                hangs up a call in progress, the other changes what an AI agent says (its greeting,
                instructions or opening line &mdash; never its voice, tools or numbers).
              </p>
              <p>
                Correcting a lead changes it here only; a lead already in your CRM is not updated
                there.
              </p>
              <p>
                It cannot make a payment or change your billing. An account owner can ask it for
                a payment link to top up the wallet; nothing is charged until someone opens the
                link and pays, and the wallet is credited only when the payment gateway confirms.
                A WhatsApp campaign it creates is a draft, and nothing is sent until you confirm the
                launch.
              </p>
            </Section>

            <Section title="Turning it off">
              <p>
                Revoke the connection from your assistant&rsquo;s connector settings, which
                immediately stops it reaching your account. The credential also expires on its own,
                so a connection you abandon does not stay live.
              </p>
            </Section>

            <Section title="Before you approve — check the name">
              <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-3">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" aria-hidden="true" />
                <p className="text-slate-700">
                  Our consent screen shows a tick beside assistants we recognise, and the tick
                  follows the application&rsquo;s domain rather than the name it gives us. If a
                  connection request claims a well-known name without that tick, the screen says so
                  &mdash; do not approve it.
                </p>
              </div>
            </Section>

            <Section title="Help">
              <p>
                Connector questions go to{' '}
                <a
                  className="font-medium text-blue-700 hover:text-blue-800"
                  href={`mailto:${connector.supportEmail}`}
                >
                  {connector.supportEmail}
                </a>
                . How we handle the data behind all of this is in our{' '}
                <Link to="/privacy" className="font-medium text-blue-700 hover:text-blue-800">
                  privacy policy
                </Link>
                .
              </p>
            </Section>
          </>
        )}

        <footer className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
          {privacy.legalName}
        </footer>
      </div>
    </div>
  );
};

export default ConnectorDocs;
